import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../../api/cliente';
import {
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  Vazio,
} from '../../componentes/base';
import { Campo, Seletor } from '../../componentes/campos';
import { Paginacao, type Paginado } from '../../componentes/Paginacao';
import { data as formatarData, documento, moeda, plural, telefone } from '../../util/formato';
import { ModalNovoCliente } from '../componentes/NovoCliente';
import { useFasesDoPortal } from '../dados';
import { useSessaoPortal } from '../SessaoPortal';

interface ClienteNaLista {
  readonly id: string;
  readonly nome: string;
  readonly documento: string | null;
  readonly telefone: string | null;
  readonly cidade: string | null;
  readonly uf: string | null;
  readonly valorOriginal: number;
  readonly descricaoDivida: string | null;
  readonly fase: string;
  readonly documentos: number;
  /** Parcela em aberto mais antiga do acordo ativo. Nula sem acordo ou sem parcela. */
  readonly proximoVencimento: string | null;
  readonly parcelasVencidas: number;
  readonly clinica: { id: string; nome: string };
}

/**
 * Lista de clientes da clínica — e somente dela.
 *
 * O isolamento não depende desta tela: o servidor põe a clínica da sessão no `where`
 * de toda consulta, e nenhum filtro daqui alcança outra carteira. O que a tela faz é
 * ajudar a encontrar um nome no meio de uma centena.
 *
 * Os filtros vivem na URL para que os cartões de pendência da tela inicial possam
 * apontar direto para a lista já filtrada.
 */
export function PaginaClientesDoPortal() {
  const { token = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [cadastrando, setCadastrando] = useState(false);
  const fases = useFasesDoPortal();
  /* Com duas razões sociais no mesmo acesso, a clínica de cada cliente deixa de
     ser detalhe: é o que o ajuizamento vai exigir depois. */
  const { clinicas } = useSessaoPortal();
  const variasCarteiras = clinicas.length > 1;

  const base = `/portal/${token}`;
  const filtro = (chave: string) => params.get(chave) ?? '';

  function definir(chave: string, valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setParams(novo, { replace: true });
    setPagina(1);
  }

  const temFiltro = busca !== '' || params.size > 0;

  const { data: lista, isLoading } = useQuery({
    queryKey: ['portal', 'devedores', pagina, busca, params.toString()],
    queryFn: () =>
      api.buscar<Paginado<ClienteNaLista>>('/portal/devedores', {
        pagina,
        limite: 25,
        busca: busca || undefined,
        fase: filtro('fase') || undefined,
        pendencia: filtro('pendencia') || undefined,
      }),
  });

  function limpar() {
    setParams(new URLSearchParams(), { replace: true });
    setBusca('');
    setPagina(1);
  }

  return (
    <>
      <CabecalhoDaPagina
        titulo="Seus clientes"
        descricao="Situação de cada caso na cobrança"
        acoes={
          <Botao tipo="primario" onClick={() => setCadastrando(true)}>
            Cadastrar cliente
          </Botao>
        }
      />

      <Cartao semPadding>
        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-[1fr_14rem_14rem]">
          <Campo
            valor={busca}
            aoMudar={(v) => {
              setBusca(v);
              setPagina(1);
            }}
            placeholder="Buscar por nome ou CPF"
          />
          <Seletor
            valor={filtro('fase')}
            aoMudar={(v) => definir('fase', v)}
            vazio="Todas as fases"
            opcoes={[...fases.values()].map((f) => ({ valor: f.fase, rotulo: f.rotulo }))}
          />
          <Seletor
            valor={filtro('pendencia')}
            aoMudar={(v) => definir('pendencia', v)}
            vazio="Todas as pendências"
            opcoes={[
              { valor: 'telefone', rotulo: 'Sem telefone' },
              { valor: 'assinaturas', rotulo: 'Aguardando assinatura' },
            ]}
          />
        </div>

        {temFiltro && (
          <div className="px-5 pb-4">
            <Botao tipo="discreto" onClick={limpar} className="px-0 text-xs">
              Limpar filtros
            </Botao>
          </div>
        )}

        {isLoading ? (
          <Carregando />
        ) : !lista || lista.itens.length === 0 ? (
          <Vazio
            titulo={
              temFiltro
                ? 'Nenhum cliente corresponde aos filtros.'
                : 'Você ainda não tem clientes em cobrança.'
            }
            saida={
              temFiltro ? (
                <Botao onClick={limpar}>Limpar filtros</Botao>
              ) : (
                <Botao tipo="primario" onClick={() => setCadastrando(true)}>
                  Cadastrar o primeiro
                </Botao>
              )
            }
          />
        ) : (
          <>
            <div className="rolagem-lateral border-t">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                    <th className="px-5 py-2.5 font-medium">Cliente</th>
                    <th className="px-3 py-2.5 font-medium">Situação</th>
                    <th className="px-3 py-2.5 text-right font-medium">Dívida</th>
                    <th className="px-5 py-2.5 text-right font-medium">Documentos</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.itens.map((c) => {
                    const fase = fases.get(c.fase);
                    return (
                      <tr
                        key={c.id}
                        className="border-b transition-colors last:border-0 hover:bg-[color:var(--superficie-afundada)]"
                      >
                        <td className="px-5 py-3">
                          <Link
                            to={`${base}/clientes/${c.id}`}
                            className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                          >
                            {c.nome}
                          </Link>
                          <p className="mt-0.5 font-mono text-[0.6875rem] text-[color:var(--texto-tenue)]">
                            {documento(c.documento)}
                            {c.telefone ? (
                              <> · {telefone(c.telefone)}</>
                            ) : (
                              <span
                                className="ml-1.5 font-sans"
                                style={{ color: 'var(--color-atencao)' }}
                              >
                                sem telefone
                              </span>
                            )}
                          </p>
                          {variasCarteiras && (
                            <p className="mt-0.5 text-[0.6875rem] text-[color:var(--texto-suave)]">
                              {c.clinica.nome}
                            </p>
                          )}
                          {c.descricaoDivida && (
                            <p className="mt-0.5 truncate text-[0.6875rem] text-[color:var(--texto-tenue)]">
                              {c.descricaoDivida}
                            </p>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {fase ? <Ficha cor={fase.cor}>{fase.rotulo}</Ficha> : c.fase}
                          {/* A DATA QUE A SITUAÇÃO ESTÁ QUERENDO DIZER. "Em atraso"
                              sozinho manda abrir a ficha para descobrir desde quando,
                              e é essa data que decide se a recepção liga hoje. */}
                          <Vencimento
                            data={c.proximoVencimento}
                            vencidas={c.parcelasVencidas}
                          />
                        </td>

                        <td className="tabular px-3 py-3 text-right">
                          {moeda(c.valorOriginal)}
                        </td>

                        <td className="px-5 py-3 text-right text-xs">
                          {c.documentos > 0 ? (
                            <span className="text-[color:var(--texto-suave)]">
                              {plural(c.documentos, 'anexo', 'anexos')}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-atencao)' }}>nenhum</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginacao
              dados={lista}
              aoMudarPagina={setPagina}
              nomeDoItem={['cliente', 'clientes']}
            />
          </>
        )}
      </Cartao>

      <ModalNovoCliente aberto={cadastrando} aoFechar={() => setCadastrando(false)} />
    </>
  );
}

/**
 * A data da parcela em aberto mais antiga, embaixo da situação.
 *
 * Vencida, diz DESDE QUANDO — é o número de dias que dá o tamanho do problema, e
 * quem tem quatro clientes em atraso precisa saber qual chamar primeiro. A vencer,
 * diz quando é, sem cor: não há nada de errado ali.
 *
 * Sem acordo ou sem parcela em aberto, não escreve nada. Não há data a afirmar, e
 * um travessão numa coluna já cheia só ocuparia espaço.
 */
export function Vencimento({ data: iso, vencidas }: { data: string | null; vencidas: number }) {
  if (!iso) return null;

  const dia = formatarData(iso);
  if (vencidas === 0) {
    return (
      <p className="mt-1 text-[0.6875rem] text-[color:var(--texto-tenue)]">vence {dia}</p>
    );
  }

  return (
    <p className="mt-1 text-[0.6875rem]" style={{ color: 'var(--color-vencido)' }}>
      vencida desde {dia}
      {vencidas > 1 && (
        <span className="text-[color:var(--texto-tenue)]">
          {' '}
          · {plural(vencidas, 'parcela vencida', 'parcelas vencidas')}
        </span>
      )}
    </p>
  );
}
